import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import { SubmissionSearchObject} from '../../components/dashboard/search-object';
import {environment} from '../../../environments/environment';

@Injectable()
export class KpiService {

    private searchKpiCriteria = new BehaviorSubject<SubmissionSearchObject>(null);
    currentKpiSearchCriteria = this.searchKpiCriteria.asObservable();

    private perYearChart = new BehaviorSubject<any>(null);
    currentPerYearChart = this.perYearChart.asObservable();

    private averagePerMonthChart = new BehaviorSubject<any>(null);
    currentAveragePerMonthChart = this.averagePerMonthChart.asObservable();

    private averagePerDayChart = new BehaviorSubject<any>(null);
    currentAveragePerDayChart = this.averagePerDayChart.asObservable();

    private perProjectChart = new BehaviorSubject<any>(null);
    currentPerProjectChart = this.perProjectChart.asObservable();

    private perCenterChart = new BehaviorSubject<any>(null);
    currentPerCenterChart = this.perCenterChart.asObservable();

    private perTestTypeChart = new BehaviorSubject<any>(null);
    currentPerTestTypeChart = this.perTestTypeChart.asObservable();

    private totalSampleSearchCount = new BehaviorSubject<any>(null);
    currentTotalSampleSearchCount = this.totalSampleSearchCount.asObservable();

    private readonly httpOptions = {
        observe: 'response',
        responseType: 'json',
        withCredentials: true
    } as const;

    private urlPrefix = environment.is_local ? 'http://localhost:8081' : '';

    constructor(private readonly http: HttpClient) {
    }

    triggerKpiSearch(search: SubmissionSearchObject, startDate: string, endDate: string) {
        if ( startDate !== '' ) {
            search.startDate = startDate;
        }
        if ( endDate !== '' ) {
            search.endDate = endDate;
        }
        this.changeSearchCriteria(search);
        this.getKpiYearsContent(search);
        this.getKpiMonthsContent(search);
        this.getKpiDaysContent(search);
        this.getKpiProjectsContent(search);
        this.getKpiCentersContent(search);
        this.getKpiTestTypeContent(search);
        this.getTotalSampleCount(search);
    }

    getKpiYearsContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/years`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changePerYear(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    getKpiMonthsContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/months`;

        return firstValueFrom(this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }))
            .then(resp => this.changeAveragePerMonth(resp))
            .catch(this.handleError);
    }

    getKpiDaysContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/days`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changeAveragePerDay(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    getKpiProjectsContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/projects`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changePerProject(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    getKpiCentersContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/centers`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changePerCenter(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    getKpiTestTypeContent(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/testType`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changePerTestType(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    getTotalSampleCount(search: SubmissionSearchObject) {
        const url = this.urlPrefix + `/dashboard/kpi/totalSamples`;

        return this.http.post<any>(url, JSON.stringify(search), { headers: {'Content-Type': 'application/json'} }).subscribe({
            next: resp => {
                this.changeTotalSampleSearchCount(resp)
            },
            error: error => {
                this.handleError(error)
            }
        })
    }

    extractData(res: any) {
        return res;
    }

    changePerYear(message: any) {
        this.perYearChart.next(message);
    }

    changeAveragePerMonth(message: any) {
        this.averagePerMonthChart.next(message);
    }

    changeAveragePerDay(message: any) {
        this.averagePerDayChart.next(message);
    }

    changePerProject(message: any) {
        this.perProjectChart.next(message);
    }

    changePerCenter(message: any) {
        this.perCenterChart.next(message);
    }

    changePerTestType(message: any) {
        this.perTestTypeChart.next(message);
    }

    changeTotalSampleSearchCount(message: any) {
        this.totalSampleSearchCount.next(message);
    }

    changeSearchCriteria(message: SubmissionSearchObject) {
        this.searchKpiCriteria.next(message);
    }

    clearSearchResults() {
        this.perYearChart.next('');
        this.averagePerMonthChart.next('');
        this.averagePerDayChart.next('');
        this.perProjectChart.next('');
        this.perCenterChart.next('');
        this.perTestTypeChart.next('');
        this.totalSampleSearchCount.next('');
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }
}
